import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Input } from '../../components';
import { PathsConfig } from '../../services/httpService/configPaths';
import { httpService } from '../../services/httpService/httpService';
import { setOpenSuccessErrorModal } from '../../storeSlices/errorSuccessSlice';
import { images } from '../../constants/images';
import { isValidEmail, isValidPassword } from '../../utils/validationUtils';
import styles from '../Login/Login.module.scss';

const initialDetails = {
	email: '',
	emailError: false,
	password: '',
	passwordError: false,
	name: '',
	nameError: false,
};

export const SandboxSignup = () => {
	const { t } = useTranslation('translation');

	const [formDetails, setFormDetails] = useState(initialDetails);
	const [errorMessage, setErrorMessage] = useState<string>();
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const validateAndSubmit = async () => {
		setErrorMessage(undefined);

		let hasError = false;
		const updatedDetails = { ...formDetails };

		if (!isValidEmail(formDetails.email)) {
			updatedDetails.emailError = true;
			hasError = true;
		}

		if (!isValidPassword(formDetails.password)) {
			updatedDetails.passwordError = true;
			hasError = true;
			setErrorMessage('Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character');
		}

		if (hasError) {
			setFormDetails(updatedDetails);
			return;
		}

		await register();
	};

	const register = async () => {
		setIsLoading(true);
		try {
			await httpService({
				dispatch,
				path: PathsConfig.sandboxRegister,
				data: {
					email: formDetails.email,
					password: formDetails.password,
					name: formDetails.name || undefined,
				},
			});
			setErrorMessage(undefined);
			// Navigate to login with success message
			navigate('/login?registered=true');
		} catch (e: any) {
			if (e.response?.data?.message) {
				setErrorMessage(e.response?.data?.message);
			} else {
				dispatch(setOpenSuccessErrorModal({ responseError: e }));
			}
		} finally {
			setIsLoading(false);
		}
	};

	const updateForm = (field: string, data: string) => {
		setFormDetails((prev) => ({ ...prev, [field]: data, [`${field}Error`]: false }));
	};

	return (
		<div className={styles.globalPageContainer}>
			<div className={styles.container} onKeyDown={(event) => event.key === 'Enter' && validateAndSubmit()}>
				<div className={styles.leftContainer}>
					<div className={styles.header}>
						<Icon imgType='logo' height={2.5} width={12.3} color='black' />
					</div>
					<div className={styles.content}>
						<div className={styles.loginContainer}>
							<div className={styles.loginTitleContainer}>
								<h1 className={styles.title}>Create Sandbox Account</h1>
								<span className={styles.description}>Set up your local sandbox credentials</span>
							</div>
							<div className={styles.loginFields}>
								<Input
									value={formDetails.email}
									onChange={(value) => updateForm('email', value)}
									title={t('email')}
									showError={formDetails.emailError}
									type='email'
									placeholder={t('enterYourEmail')}
								/>
								<Input
									value={formDetails.name}
									onChange={(value) => updateForm('name', value)}
									title='Name (optional)'
									type='text'
									placeholder='Enter your name'
								/>
								<Input
									value={formDetails.password}
									onChange={(value) => updateForm('password', value)}
									title={t('password')}
									showError={formDetails.passwordError}
									type={showPassword ? undefined : 'password'}
									placeholder='••••••••••'
									rightChildren={
										<Button type='link' color='neutral' onClick={() => setShowPassword(!showPassword)} className={styles.eyeButton}>
											<Icon imgType={showPassword ? 'closed_eye' : 'eye'} />
										</Button>
									}
								/>
								<Button className={styles.signInButton} onClick={validateAndSubmit} disabled={isLoading}>
									{isLoading ? 'Creating account...' : 'Create Sandbox Account'}
								</Button>
								<div style={{ textAlign: 'center' }}>
									<span className={styles.description}>Already have an account? </span>
									<Button type='link' onClick={() => navigate('/login')}>
										Sign In
									</Button>
								</div>
							</div>
							<div className={styles.errorBox} style={{ visibility: errorMessage ? 'visible' : 'hidden' }}>
								<Icon imgType='warning_sign' width={2} color='destructive' />
								<span>{errorMessage}</span>

								<Button type='link' color='neutral' onClick={() => setErrorMessage(undefined)}>
									<Icon imgType='x_icon' color='destructive' className={styles.x_icon} />
								</Button>
							</div>
						</div>
					</div>
				</div>
				<div className={styles.rightContainer}>
					<img alt='signup' src={images.login} className={styles.image} />
				</div>
			</div>
		</div>
	);
};
